'use client';
import type { Event } from '../../interface/event';
import Link from 'next/link';
import { FC } from 'react';
import {
  CheckCircle,
  ChevronRight,
  ShareOutline,
} from '@eqi-investimentos/icons/web';
import { Button, Center, Flex, Text } from '@eqi-investimentos/react';
import { Divider, Spinner } from '@chakra-ui/react';
import { useT } from '@/libs/i18n/utils/useT';
import { PATHS } from '@/config/paths';
import { TextOverflow } from '@/components/elements/TextOverflow';
import { Thumb } from '@/components/elements/Thumb';
import { IfRender } from '@/utils/jsx';

export const CardEvent: FC<CardEventProps> = ({
  isLoading = false,
  event,
  isPast = false,
  requestsubscription,
  shareEvent,
}) => {
  const t = useT();

  const linkHref = linkPerTypeEvent[
    event?.link_type as keyof typeof linkPerTypeEvent
  ]
    ? linkPerTypeEvent[event?.link_type as keyof typeof linkPerTypeEvent](event)
    : linkPerTypeEvent.default(event);

  return (
    <Flex
      position="relative"
      flexDir="column"
      w="19.375rem"
      h="auto"
      maxH="36rem"
      p="l"
      gap="l"
      border="1px solid"
      borderColor="border.secondary"
      bg="background.primary"
      borderRadius="xs"
    >
      <Flex
        w="full"
        h="10.6875rem"
        sx={{
          '>div,img': {
            w: '100% !important',
            h: '172px !important',
            maxH: '172px !important',
            borderRadius: '0.279375rem',
          },
        }}
      >
        <Thumb src={event?.thumb} full alt={event?.title} fit="cover" />
      </Flex>

      <Flex flexDir="column" gap="xs">
        <Flex w="full" h="2.7rem" flexDir="column" justifyContent="center">
          <Text size="xl" fontWeight="normal" color="content.primary">
            {event?.title}
          </Text>
        </Flex>

        <Flex w="full" h="1.75rem" flexDir="row" gap="0.5rem" mb="2">
          <Text size="l" fontWeight="normal" color="content.tertiary">
            {`${event?.period} • ${event?.hours} • ${event?.type}`}
          </Text>
        </Flex>

        <Flex w="full" h="5.6rem">
          <TextOverflow
            charLimit={120}
            h="7.5rem"
            fontSize="md"
            fontWeight="normal"
            color="content.secondary"
          >
            {event?.summary}
          </TextOverflow>
        </Flex>
      </Flex>

      <Center w="full">
        <Divider
          my="0.5rem"
          borderTop="1px solid"
          borderColor="border.secondary"
          opacity={1}
        />
      </Center>

      <Flex w="full" h="3rem" flexDir="row" justifyContent="center">
        <IfRender condition={isPast}>
          <Flex
            aria-label="button-event-watch"
            as={Link}
            href={linkHref}
            target={event?.link_type === 'external' ? '_blank' : '_self'}
            rel="noopener noreferrer"
            w="full"
            h="3rem"
            justifyContent="center"
            alignItems="center"
            fontSize="sm"
            color="content.alwaysLight"
            bg="content.brand"
            fontWeight="semibold"
            border="none"
            borderRadius="0.25rem"
            transition="background-color 0.2s"
            _hover={{
              bg: 'action.hover.brand',
            }}
          >
            {t('attributes.toWatch')}
          </Flex>
        </IfRender>
        <IfRender condition={!event?.has_marked && !isPast}>
          <Button
            aria-label="button-event-participate"
            variant="solid"
            w="full"
            h="3rem"
            onClick={() =>
              !requestsubscription ? null : requestsubscription(event?.id)
            }
          >
            {!isLoading && t('screens.events.wantParticipate')}
            {isLoading && <Spinner size="sm" color="content.alwaysLight" />}
          </Button>
        </IfRender>
        <IfRender condition={event?.has_marked && !isPast}>
          <Flex
            w="full"
            flexDir="row"
            justifyContent="start"
            alignItems="center"
            gap="0.5rem"
          >
            <CheckCircle width={24} height={24} color="alert.success" />
            <Text size="xl" fontWeight="normal" color="content.primary">
              {t('screens.events.confirmedSub')}
            </Text>
          </Flex>
        </IfRender>
      </Flex>

      <IfRender condition={!isPast}>
        <Flex w="full" h="1.75rem" justifyContent="space-between" gap="l">
          <Flex
            as={Link}
            href={linkHref}
            target={event?.link_type === 'external' ? '_blank' : '_self'}
            rel="noopener noreferrer"
            flexDir="row"
            justifyContent="center"
            alignItems="center"
            gap="2xs"
          >
            <Text size="l" fontWeight="normal" color="content.primary">
              {`${t('attributes.see')} ${t('attributes.more')}`}
            </Text>
            <ChevronRight width={24} height={24} color="content.brand" />
          </Flex>

          <Flex
            aria-label="button-event-share"
            as="button"
            flexDir="row"
            justifyContent="center"
            alignItems="center"
            gap="2xs"
            onClick={() => (!shareEvent ? null : shareEvent(linkHref))}
          >
            <Text size="l" fontWeight="normal" color="content.primary">
              {t('attributes.share')}
            </Text>
            <ShareOutline width={24} height={24} color="content.brand" />
          </Flex>
        </Flex>
      </IfRender>
    </Flex>
  );
};

interface CardEventProps {
  isLoading?: boolean;
  event: Event;
  isPast?: boolean;
  requestsubscription?: (eventId: string) => void;
  shareEvent?: (link: string) => void;
}

const linkPerTypeEvent = {
  content: (data: Event) =>
    `${PATHS.hubs.content}/${data?.content?.slug ? data?.content?.slug : data?.content?.id}`,
  program: (data: Event) =>
    `${PATHS.hubs.program}/${data?.program?.slug ? data?.program?.slug : data?.program?.id}`,
  author: (data: Event) =>
    `${PATHS.hubs.author}/${data?.author?.slug ? data?.author?.slug : data?.author?.id}`,
  external: (data: Event) => `${data?.destination_link}`,
  default: (data: Event) => `${data?.url}`,
};
